import { findBloodRelationshipPaths } from '../../backend/relationshipPaths.js';

function linkEndpointId(endpoint) {
    return endpoint?.id || endpoint;
}

function normalizeFamilyName(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');
}

/** Build claims behind an anchor, keeping central junctions as gateways. */
export function buildParentalGatewaySubclade({
    nodes = [], links = [], anchorId, stopIds = [], anchorIds = [],
    includeAnchorHouseholds = false, relations = ['parent', 'marriage'],
} = {}) {
    const nodeById = new Map(nodes.filter(node => node?.id).map(node => [node.id, node]));
    const allowed = new Set(relations);
    const parents = new Map();
    const children = new Map();
    const partners = new Map();
    const graph = new Map([...nodeById.keys()].map(id => [id, new Set()]));
    const add = (map, from, to) => {
        if (!map.has(from)) map.set(from, new Set());
        map.get(from).add(to);
    };
    for (const link of links) {
        const source = linkEndpointId(link?.source);
        const target = linkEndpointId(link?.target);
        if (!graph.has(source) || !graph.has(target) || source === target || !allowed.has(link.relation)) continue;
        add(graph, source, target);
        add(graph, target, source);
        if (link.relation === 'parent') {
            add(parents, target, source);
            add(children, source, target);
        } else if (link.relation === 'marriage') {
            add(partners, source, target);
            add(partners, target, source);
        }
    }
    const parentIds = new Set(parents.get(anchorId) || []);
    const hardStops = new Set([...stopIds, ...anchorIds].filter(id => id !== anchorId));
    const boundaryIds = new Set([
        ...hardStops,
        ...[...(graph.get(anchorId) || [])].filter(id => !parentIds.has(id)),
    ]);
    const memberIds = new Set();
    const ancestryMemberIds = new Set();
    const directAncestryMemberIds = new Set();
    const householdMemberIds = new Set();
    const claimDistances = new Map();
    const directAncestryDistances = new Map();
    const householdDistances = new Map();
    const familyName = nodeById.get(anchorId)?.lastname || '';
    const result = { anchorId, parentIds, boundaryIds, memberIds, ancestryMemberIds,
        directAncestryMemberIds, householdMemberIds, familyName, claimDistances,
        directAncestryDistances, householdDistances };
    if (!nodeById.has(anchorId) || !parentIds.size) return result;

    const sharedChildren = (first, second) => [...(children.get(first) || [])]
        .filter(id => children.get(second)?.has(id));
    // A household joining two named anchors is a junction, not a side branch.
    for (const partner of partners.get(anchorId) || []) {
        if (hardStops.has(partner)) sharedChildren(anchorId, partner).forEach(id => boundaryIds.add(id));
    }
    const ancestorQueue = [anchorId];
    directAncestryDistances.set(anchorId, 0);
    for (let cursor = 0; cursor < ancestorQueue.length; cursor += 1) {
        const current = ancestorQueue[cursor];
        directAncestryMemberIds.add(current);
        for (const parent of parents.get(current) || []) {
            if (boundaryIds.has(parent) || directAncestryDistances.has(parent)) continue;
            directAncestryDistances.set(parent, directAncestryDistances.get(current) + 1);
            ancestorQueue.push(parent);
        }
    }

    const descendants = [];
    const claimHousehold = (id, distance, expand = false) => {
        if (hardStops.has(id) || id === anchorId) return;
        if (!householdDistances.has(id) || householdDistances.get(id) > distance) {
            householdDistances.set(id, distance);
            householdMemberIds.add(id);
            if (expand) descendants.push(id);
        }
    };
    if (includeAnchorHouseholds) {
        const anchorSurname = normalizeFamilyName(familyName);
        for (const partner of partners.get(anchorId) || []) {
            if (hardStops.has(partner)) continue;
            const shared = sharedChildren(anchorId, partner).filter(id => !hardStops.has(id));
            if (!anchorSurname || !shared.some(id => normalizeFamilyName(nodeById.get(id)?.lastname) === anchorSurname)) continue;
            claimHousehold(partner, 1);
            shared.forEach(id => claimHousehold(id, 1, true));
            for (const child of children.get(partner) || []) {
                if (!shared.includes(child)) boundaryIds.add(child);
            }
        }
        for (let cursor = 0; cursor < descendants.length; cursor += 1) {
            const current = descendants[cursor];
            const distance = householdDistances.get(current);
            const blockedChildren = new Set();
            for (const partner of partners.get(current) || []) {
                if (hardStops.has(partner)) {
                    sharedChildren(current, partner).forEach(id => {
                        blockedChildren.add(id);
                        boundaryIds.add(id);
                    });
                } else {
                    claimHousehold(partner, distance + 1);
                }
            }
            for (const child of children.get(current) || []) {
                if (!blockedChildren.has(child) && !boundaryIds.has(child)) claimHousehold(child, distance + 1, true);
            }
        }
    }
    // A blocked junction can also be reached through another marriage in a cycle.
    for (const id of boundaryIds) {
        if (!householdMemberIds.has(id)) continue;
        // Original anchor households are intentional exceptions to immediate-neighbour gateways.
        if (hardStops.has(id) || !(graph.get(anchorId)?.has(id))) {
            householdMemberIds.delete(id);
            householdDistances.delete(id);
        }
    }
    const visit = (seeds, distances, destination) => {
        const queue = [...seeds];
        for (let cursor = 0; cursor < queue.length; cursor += 1) {
            const current = queue[cursor];
            destination.add(current);
            for (const neighbor of graph.get(current) || []) {
                if (neighbor === anchorId || hardStops.has(neighbor)
                    || (boundaryIds.has(neighbor) && !householdMemberIds.has(neighbor)) || distances.has(neighbor)) continue;
                distances.set(neighbor, distances.get(current) + 1);
                queue.push(neighbor);
            }
        }
    };
    const ancestryDistances = new Map([...parentIds]
        .filter(id => !boundaryIds.has(id)).map(id => [id, 1]));
    visit(ancestryDistances.keys(), ancestryDistances, ancestryMemberIds);
    claimDistances.set(anchorId, 0);
    // Include a partner's collateral family as reachability evidence, not direct lineage.
    visit([anchorId], claimDistances, memberIds);
    ancestryMemberIds.forEach(id => memberIds.add(id));
    memberIds.add(anchorId);
    return result;
}

/** Exclusive ownership by evidence and route length; equal claims stay in the core. */
export function partitionOverlappingSubclades({ subclades = [], links = [], relations = ['parent', 'marriage'] } = {}) {
    const normalized = subclades.map(subclade => ({ ...subclade, memberIds: [...(subclade.memberIds || [])] }));
    const allowedRelations = new Set(relations);
    const candidates = new Map();
    for (const subclade of normalized) {
        const members = new Set(subclade.memberIds);
        const direct = new Set(subclade.directAncestryMemberIds || []);
        const households = new Set(subclade.householdMemberIds || []);
        const distances = new Map([[subclade.anchorId, 0]]);
        const adjacency = buildUndirectedAdjacency([...members].map(id => ({ id })), links.filter(link => (
            allowedRelations.has(link.relation) && members.has(linkEndpointId(link.source)) && members.has(linkEndpointId(link.target))
        )));
        const queue = [subclade.anchorId];
        for (let cursor = 0; cursor < queue.length; cursor += 1) {
            for (const neighbor of adjacency.get(queue[cursor]) || []) {
                if (distances.has(neighbor)) continue;
                distances.set(neighbor, distances.get(queue[cursor]) + 1);
                queue.push(neighbor);
            }
        }
        for (const id of members) {
            const priority = id === subclade.anchorId ? 3 : direct.has(id) ? 2 : households.has(id) ? 1 : 0;
            const evidenceDistances = priority === 2 ? subclade.directAncestryDistances
                : priority === 1 ? subclade.householdDistances : subclade.claimDistances;
            const distance = evidenceDistances?.get(id) ?? distances.get(id) ?? Infinity;
            if (!candidates.has(id)) candidates.set(id, []);
            candidates.get(id).push({ owner: subclade.id, priority, distance });
        }
    }
    const owners = new Map();
    for (const [id, claims] of candidates) {
        claims.sort((a, b) => b.priority - a.priority || a.distance - b.distance);
        const [first, second] = claims;
        if (!second || first.priority !== second.priority || first.distance !== second.distance) owners.set(id, first.owner);
    }
    return normalized.map(subclade => ({ ...subclade, allMemberIds: subclade.memberIds,
        memberIds: subclade.memberIds.filter(id => owners.get(id) === subclade.id) }));
}

function shortestAngleGap(first, second) {
    const twoPi = 2 * Math.PI;
    const difference = Math.abs(first - second) % twoPi;
    return Math.min(difference, twoPi - difference);
}

function requiredSharedRingRadius(groups, minimumRadius, padding) {
    let radius = minimumRadius;
    for (let first = 0; first < groups.length; first += 1) {
        for (let second = first + 1; second < groups.length; second += 1) {
            const angleGap = Math.max(0.001, shortestAngleGap(
                groups[first].angle,
                groups[second].angle,
            ));
            const chordFactor = Math.max(0.001, 2 * Math.sin(angleGap / 2));
            radius = Math.max(
                radius,
                (groups[first].radius + groups[second].radius + padding) / chordFactor,
            );
        }
    }
    return radius;
}

function requiredOuterRingRadius(innerGroups, outerGroups, innerRingRadius, minimumRadius, padding) {
    let outerRingRadius = minimumRadius;
    for (const inner of innerGroups) {
        for (const outer of outerGroups) {
            const angleGap = shortestAngleGap(inner.angle, outer.angle);
            const minimumDistance = inner.radius + outer.radius + padding;
            const perpendicularDistance = innerRingRadius * Math.sin(angleGap);
            const discriminant = minimumDistance ** 2 - perpendicularDistance ** 2;
            if (discriminant <= 0) continue;
            const candidate = innerRingRadius * Math.cos(angleGap) + Math.sqrt(discriminant);
            outerRingRadius = Math.max(outerRingRadius, candidate);
        }
    }
    return outerRingRadius;
}

/**
 * Place subclades on one or two collision-safe rings. Larger networks use a
 * paired two-ring arrangement: the smaller group in each angular pair moves
 * inward and the larger group stays outside. This fills the main-network
 * centre without losing the stable semantic direction of each subclade.
 */
export function buildSeparatedRadialTargets({
    subclades = [],
    baseRadius = 0,
    innerRadius = 0,
    padding = 48,
    radiusPerMember = 12,
    minimumGroupRadius = 68,
    maximumSingleRingGroups = 7,
} = {}) {
    const twoPi = 2 * Math.PI;
    const groups = subclades
        .map((subclade) => {
            const rawAngle = Number(subclade?.angle);
            const memberCount = subclade?.memberIds instanceof Set
                ? subclade.memberIds.size
                : Array.isArray(subclade?.memberIds) ? subclade.memberIds.length : 0;
            if (!subclade?.id || !Number.isFinite(rawAngle) || memberCount < 1) return null;
            return {
                id: subclade.id,
                angle: ((rawAngle % twoPi) + twoPi) % twoPi,
                memberCount,
                radius: Math.max(minimumGroupRadius, Number(subclade.measuredRadius) || Math.sqrt(memberCount) * radiusPerMember),
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.angle - b.angle || String(a.id).localeCompare(String(b.id)));

    const maxGroupRadius = groups.reduce((maximum, group) => Math.max(maximum, group.radius), 0);
    if (groups.length <= maximumSingleRingGroups) {
        const ringRadius = requiredSharedRingRadius(
            groups,
            Math.max(0, baseRadius, innerRadius + maxGroupRadius + padding),
            padding,
        );
        const targets = new Map(groups.map(group => [group.id, {
            dx: Math.cos(group.angle) * ringRadius,
            dy: Math.sin(group.angle) * ringRadius,
            radius: group.radius,
            memberCount: group.memberCount,
            ring: 'outer',
        }]));

        return {
            ringRadius,
            innerRingRadius: null,
            outerRingRadius: ringRadius,
            outerRadius: ringRadius + maxGroupRadius + padding,
            targets,
        };
    }

    const innerGroups = [];
    const outerGroups = [];
    for (let index = 0; index < groups.length; index += 2) {
        const first = groups[index];
        const second = groups[index + 1];
        if (!second) {
            (innerGroups.length <= outerGroups.length ? innerGroups : outerGroups).push(first);
            continue;
        }
        const firstIsSmaller = first.radius < second.radius
            || (first.radius === second.radius && first.memberCount <= second.memberCount);
        innerGroups.push(firstIsSmaller ? first : second);
        outerGroups.push(firstIsSmaller ? second : first);
    }

    const maxInnerRadius = innerGroups.reduce((maximum, group) => Math.max(maximum, group.radius), 0);
    const maxOuterRadius = outerGroups.reduce((maximum, group) => Math.max(maximum, group.radius), 0);
    const innerRingRadius = requiredSharedRingRadius(
        innerGroups,
        Math.max(0, baseRadius * 0.56, innerRadius + maxInnerRadius + padding),
        padding,
    );
    const outerMinimum = Math.max(baseRadius, innerRingRadius + padding);
    const outerRingRadius = requiredOuterRingRadius(
        innerGroups,
        outerGroups,
        innerRingRadius,
        requiredSharedRingRadius(outerGroups, outerMinimum, padding),
        padding,
    );

    const targets = new Map();
    innerGroups.forEach(group => targets.set(group.id, {
        dx: Math.cos(group.angle) * innerRingRadius,
        dy: Math.sin(group.angle) * innerRingRadius,
        radius: group.radius,
        memberCount: group.memberCount,
        ring: 'inner',
    }));
    outerGroups.forEach(group => targets.set(group.id, {
        dx: Math.cos(group.angle) * outerRingRadius,
        dy: Math.sin(group.angle) * outerRingRadius,
        radius: group.radius,
        memberCount: group.memberCount,
        ring: 'outer',
    }));

    return {
        ringRadius: outerRingRadius,
        innerRingRadius,
        outerRingRadius,
        outerRadius: Math.max(
            innerRingRadius + maxInnerRadius + padding,
            outerRingRadius + maxOuterRadius + padding,
        ),
        targets,
    };
}

export function buildUndirectedAdjacency(nodes = [], links = []) {
    const graph = new Map(nodes.map(node => [node.id, []]));

    for (const link of links) {
        const source = linkEndpointId(link?.source);
        const target = linkEndpointId(link?.target);
        if (!source || !target || source === target) continue;
        if (!graph.has(source)) graph.set(source, []);
        if (!graph.has(target)) graph.set(target, []);
        graph.get(source).push(target);
        graph.get(target).push(source);
    }

    for (const [id, neighbors] of graph) {
        graph.set(id, [...new Set(neighbors)].sort((a, b) => String(a).localeCompare(String(b))));
    }
    return graph;
}

/** Build a reusable relationship topology from the privacy-filtered network. */
export function buildPersonRelationshipGraph(nodes = [], links = []) {
    const nodeIds = new Set(nodes.map(node => node.id));
    const familyRelations = new Set(['parent', 'marriage', 'sibling', 'related']);
    const familyLinks = links.filter(link => (
        familyRelations.has(link.relation || 'related')
        && nodeIds.has(linkEndpointId(link.source))
        && nodeIds.has(linkEndpointId(link.target))
    ));
    const parentSets = new Map();
    for (const link of familyLinks) {
        if (link.relation !== 'parent') continue;
        const parent = linkEndpointId(link.source);
        const child = linkEndpointId(link.target);
        if (parent === child) continue;
        if (!parentSets.has(child)) parentSets.set(child, new Set());
        parentSets.get(child).add(parent);
    }
    return {
        parentsByPerson: new Map([...parentSets].map(([id, parents]) => [
            id, [...parents].sort((a, b) => String(a).localeCompare(String(b))),
        ])),
        adjacency: buildUndirectedAdjacency(nodes, familyLinks),
    };
}

/** Prefer one blood relationship; otherwise show one shortest family connection. */
export function findLogicalRelationshipPath({ graph, fromId, targetId, maxDepth = 25 } = {}) {
    const adjacency = graph?.adjacency;
    if (!fromId || !targetId || !adjacency?.has(fromId) || !adjacency.has(targetId)) {
        return { path: [], kind: null };
    }

    // Search from Jeffrey, just like /relationship/I43/:personID, then reverse
    // for display. Sharing the API search keeps common-ancestor choices aligned.
    const blood = findBloodRelationshipPaths(graph, targetId, fromId, maxDepth, 1);
    if (blood.paths.length) {
        return { path: blood.paths[0].map(node => node.personID).reverse(), kind: 'blood' };
    }

    // The network draws parent/child edges explicitly, so sibling connections
    // run through a visible parent instead of introducing a synthetic edge.
    const towardTarget = new Map([[targetId, null]]);
    const queue = [{ id: targetId, depth: 0 }];
    for (let cursor = 0; cursor < queue.length; cursor += 1) {
        const { id, depth } = queue[cursor];
        if (id === fromId) {
            const path = [];
            for (let step = fromId; step != null; step = towardTarget.get(step)) path.push(step);
            return { path, kind: 'family' };
        }
        if (depth >= maxDepth) continue;
        for (const neighbor of adjacency.get(id) || []) {
            if (towardTarget.has(neighbor)) continue;
            towardTarget.set(neighbor, id);
            queue.push({ id: neighbor, depth: depth + 1 });
        }
    }
    return { path: [], kind: null };
}

function isLivingNode(node) {
    return node?.living === true
        || Number(node?.living) === 1
        || node?.privacyStatus === 'living'
        || Boolean(node?.placeholder && node?.privacyStatus !== 'deceased');
}

export function removeLivingOnlyComponents(nodes = [], links = []) {
    const validNodes = nodes.filter(node => node?.id);
    const graph = buildUndirectedAdjacency(validNodes, links);
    const nodeById = new Map(validNodes.map(node => [node.id, node]));
    const visited = new Set();
    const removedIds = new Set();

    for (const node of validNodes) {
        if (visited.has(node.id)) continue;
        const componentIds = [];
        const queue = [node.id];
        visited.add(node.id);
        let cursor = 0;
        while (cursor < queue.length) {
            const current = queue[cursor];
            cursor += 1;
            componentIds.push(current);
            for (const neighbor of graph.get(current) || []) {
                if (visited.has(neighbor)) continue;
                visited.add(neighbor);
                queue.push(neighbor);
            }
        }

        if (componentIds.length && componentIds.every(id => isLivingNode(nodeById.get(id)))) {
            componentIds.forEach(id => removedIds.add(id));
        }
    }

    return {
        nodes: validNodes.filter(node => !removedIds.has(node.id)),
        links: links.filter((link) => {
            const source = linkEndpointId(link?.source);
            const target = linkEndpointId(link?.target);
            return !removedIds.has(source) && !removedIds.has(target);
        }),
        removedIds,
    };
}
